import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import * as moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ModalAddCodeComponent } from 'src/app/components/crud/modal-add-code/modal-add-code.component';
import { environment } from 'src/app/environments/environments';
import { AccountingGroup, CodeFamily, CodeLine, CodeSubFamily, CodeType, CommercialGroup, ExecutiveGroup, FinancialGroup, ManagementMethod, Origin, Warehouse } from 'src/app/models/interfaces.model';
import { GenericCrudService } from 'src/app/services/crud/generic-crud.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-code-request',
  templateUrl: './code-request.component.html',
  styleUrls: ['./code-request.component.css']
})
export class CodeRequestComponent {
  requestCodeForm: FormGroup;
  modalRef: MdbModalRef<ModalAddCodeComponent> | null = null;
  actualId: number = 0;
  loadingFull: boolean = true;
  loading: any;
  selectedValue: number = 0;
  dateNow: string = '';
  requesterUser: string= '';

  headers: any = [
    {
      label: 'Código',
      field: 'serial',
      disableSort: false,
    },
    {
      label: 'Descripción',
      field: 'description',
      disableSort: false,
    },
    {
      label: 'Extranjera',
      field: 'foreign',
      disableSort: false,
    },
    { 
      label: 'UN', 
      field: '', 
      disableSort: false 
    },
    { 
      label: 'UN EST', 
      field: '', 
      disableSort: false 
    },
    { 
      label: 'UN FOR', 
      field: '', 
      disableSort: false 
    },
    { 
      label: 'Factor', 
      field: '', 
      disableSort: false 
    },
    { 
      label: 'Gestión SKU', 
      field: '', 
      disableSort: false 
    },
    { 
      label: 'Acciones', 
      field: '', 
      disableSort: false 
    },
  ];
  
  accountingGroup: AccountingGroup[] = [];
  commercialGroup: CommercialGroup[] = [];
  executiveGroup: ExecutiveGroup[] = [];
  financialGroup: FinancialGroup[] = [];
  managementMethods: ManagementMethod[] = [];
  origins: Origin[] = [];
  warehouses: Warehouse[] = [];
  codeTypes: CodeType[] = [];
  codeLines: CodeLine[] = [];
  codeFamilies: CodeFamily[] = [];
  codeSubFamilies: CodeSubFamily[] = [];
  
  accountingGroupSelected: string | null = null;
  comercialGroupSelected: string | null = null;
  financialGroupSelected: string | null = null;
  executiveGroupSelected: string | null = null;
  codeTypeSelected: string | null = null;
  codeLineSelected: string | null = null;
  codeFamilySelected: string | null = null;
  codeSubFamilySelected: string | null = null;
  inventoryItemCheck: boolean = false;
  inventorySalesCheck: boolean = false;
  inventoryShoppingCheck: boolean = false;
  managementMethodSelected: string | null = null;
  originSelected: string | null = null;
  warehouseSelected: string | null = null;
  searchCode: string | null = null;
  codes: any = [];
  
  constructor(
    private fb: FormBuilder,
    private modalService: MdbModalService,
    private crudService: GenericCrudService<any>
  ) {
    this.requestCodeForm = this.fb.group({
      accountingGroupSelected: [null, [this.validateSelectedOptions]],
      comercialGroupSelected: [null, [this.validateSelectedOptions]],
      financialGroupSelected: [null, [this.validateSelectedOptions]],
      executiveGroupSelected: [null, [this.validateSelectedOptions]],
      codeTypeSelected: [null, [this.validateSelectedOptions]],
      codeLineSelected: [null, [this.validateSelectedOptions]],
      codeFamilySelected: [null, [this.validateSelectedOptions]],
      codeSubFamilySelected: [null, [this.validateSelectedOptions]],
      inventoryItemCheck: [false],
      inventorySalesCheck: [false],
      inventoryShoppingCheck: [false],
      managementMethodSelected: [null, [this.validateSelectedOptions]],
      originSelected: [null, [this.validateSelectedOptions]],
      warehouseSelected: [null, [this.validateSelectedOptions]],
      codes: [null, [this.validateCodesArray]],
      searchCode: [],
    });
  }
  
  validateChecks(): boolean {
    const inventoryItemCheck = this.requestCodeForm.value.inventoryItemCheck;
    const inventorySalesCheck = this.requestCodeForm.value.inventorySalesCheck;
    const inventoryShoppingCheck = this.requestCodeForm.value.inventoryShoppingCheck;
    return inventoryItemCheck == true || inventorySalesCheck == true || inventoryShoppingCheck == true ? true : false;
  }

  async ngOnInit(): Promise<void> {
    try {
      this.requestCodeForm.get('codeLineSelected')?.disable();
      this.requestCodeForm.get('codeFamilySelected')?.disable();
      this.requestCodeForm.get('codeSubFamilySelected')?.disable();
      this.dateNow = moment(new Date()).format('DD-MM-YYYY');
      this.loadingFull = true;
      await Promise.all([
        this.getRequesterUser(),
        this.getAccountGroups(),
        this.getCommercialGroups(),
        this.getExecutiveGroups(),
        this.getFinancialGroups(),
        this.getManagementMethods(),
        this.getOrigins(),
        this.getWarehouses(),
        this.getCodeTypes(),
      ]);
    } catch (error) {
      console.log("[ngOnInit] error: ", error);
    } finally {
      this.loadingFull = false;
    }
  }

  async getRequesterUser(): Promise<void> {
    const idUser = localStorage.getItem('IdUser') ?? '';
    const body = new FormData();
    body.append('action', 'SELECT');
    body.append('table_name', 'USERS');
    body.append('id', idUser);
  
    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if(response && response.status === 1 && response.data) {
        this.requesterUser = response.data[0].SAMACCOUNTNAME;
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  }

  async getAccountGroups(): Promise<void> {
    const body = new FormData();
    body.append('action', "SELECT");
    body.append('table_name', "ACCOUNTING_GROUP");

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if(response && response.status === 1 && response.data) {
        this.accountingGroup = response.data;
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  }

  async getCommercialGroups(): Promise<void> {
    const body = new FormData();
    body.append('action', "SELECT");
    body.append('table_name', "COMMERCIAL_GROUP");

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if(response && response.status === 1 && response.data) {
        this.commercialGroup = response.data;
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  }

  async getExecutiveGroups(): Promise<void> {
    const body = new FormData();
    body.append('action', "SELECT");
    body.append('table_name', "EXECUTIVE_GROUP");

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if(response && response.status === 1 && response.data) {
        this.executiveGroup = response.data;
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  }

  async getFinancialGroups(): Promise<void> {
    const body = new FormData();
    body.append('action', "SELECT");
    body.append('table_name', "FINANCIAL_GROUP");

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if(response && response.status === 1 && response.data) {
        this.financialGroup = response.data;
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  }

  async getManagementMethods(): Promise<void> {
    const body = new FormData();
    body.append('action', "SELECT");
    body.append('table_name', "MANAGEMENT_METHOD");

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if(response && response.status === 1 && response.data) {
        this.managementMethods = response.data;
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  }

  async getOrigins(): Promise<void> {
    const body = new FormData();
    body.append('action', "SELECT");
    body.append('table_name', "ORIGIN");

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if(response && response.status === 1 && response.data) {
        this.origins = response.data;
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  }

  async getCodeTypes(): Promise<void> {
    const body = new FormData();
    body.append('action', "SELECT");
    body.append('table_name', "CODE_TYPE");

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if(response && response.status === 1 && response.data) {
        this.codeTypes = response.data;
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  }

  async getWarehouses(): Promise<void> {
    const body = new FormData();
    body.append('action', "SELECT");
    body.append('table_name', "WAREHOUSE");

    try {
      const response: any = await firstValueFrom(
        this.crudService.crud(body, environment.GenericCrudOperation)
      );
      if(response && response.status === 1 && response.data) {
        this.warehouses = response.data;
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  }

  async getCodeLines(code_type_id: any): Promise<boolean> {
    const body = new FormData();
    body.append('action', "SELECT");
    body.append('table_name', "CODE_LINE");
    body.append('code_type_id', code_type_id);

    const response: any = await firstValueFrom(
      this.crudService.crud(body, environment.GetCodeLines)
    );
    if(response && response.status === 1 && response.data) {
      this.codeLines = response.data;
      return true;
    }
    return false;
  }

  async getCodeFamilies(code_line_id: any): Promise<boolean> {
    const body = new FormData();
    body.append('action', "SELECT");
    body.append('table_name', "CODE_FAMILY");
    body.append('code_line_id', code_line_id);

    const response: any = await firstValueFrom(
      this.crudService.crud(body, environment.GetCodeFamilies)
    );
    if(response && response.status === 1 && response.data) {
      this.codeFamilies = response.data;
      return true;
    }
    return false;
  }

  async getCodeSubFamilies(code_family_id: any): Promise<boolean> {
    const body = new FormData();
    body.append('action', "SELECT");
    body.append('table_name', "CODE_SUBFAMILY");
    body.append('code_family_id', code_family_id);

    const response: any = await firstValueFrom(
      this.crudService.crud(body, environment.GetCodeSubFamilies)
    );
    if(response && response.status === 1 && response.data) {
      this.codeSubFamilies = response.data;
      return true;
    }
    return false;
  }

  addOrEditCode(mode: 'add' | 'edit', id?: number): void {
    const searchRegister = this.findCodeById(id);
    const modalData = this.getModalData(mode, searchRegister);

    this.modalRef = this.modalService.open(ModalAddCodeComponent, {
      modalClass: 'modal-xl',
      data: modalData,
      ignoreBackdropClick: true,
    });

    this.modalRef.onClose.subscribe((modalData: any) => {
      if (!modalData) return;

      const newRegister = this.createNewRegister(modalData, id);
      if (mode === 'add') {
        this.addCode(newRegister);
      } else if (mode === 'edit') {
        this.editCode(id, modalData);
      }
    });
  }
  
  private findCodeById(id?: number): any {
    return this.codes.find((item: any) => item.id === id);
  }
  
  private getModalData(mode: 'add' | 'edit', searchRegister: any): any {
    return {
      modalTitle: mode === 'edit' ? 'Editar Código' : 'Nuevo Código',
      serial: searchRegister ? searchRegister.serial : null,
      description: searchRegister ? searchRegister.description : null,
      foreign: searchRegister ? searchRegister.foreign : null,
      un: searchRegister ? searchRegister.un : null,
      un_est: searchRegister ? searchRegister.un_est : null,
      un_for: searchRegister ? searchRegister.un_for : null,
      factor: searchRegister ? searchRegister.factor : null,
      sku_management: searchRegister ? searchRegister.sku_management : null,
    };
  }
  
  private createNewRegister(modalData: any, id?: number): any {
    this.actualId++;
    return { ...modalData, id: id ?? this.actualId };
  }
  
  private addCode(newRegister: any): void {
    this.codes.push(newRegister);
  }
  
  private editCode(id: number | undefined, modalData: any): void {
    const findCode = this.findCodeById(id);
    if (findCode) {
      Object.assign(findCode, modalData);
    }
  }
  
  deleteObject(id: number, serial: string) {
    Swal.fire({
      title: "Está a punto de eliminar el código con serial: "+serial+", ¿Está seguro de realizar esta acción?",
      icon: 'warning',
      width: 800,
      customClass: {
        popup: 'custom-swal-popup'
      },
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí',
      cancelButtonText: 'Cancelar',
      allowOutsideClick: false
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Procesando...',
          allowOutsideClick: false,
          showCancelButton: false,
          showCloseButton: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
            this.codes = this.codes.filter((code: any) => code.id !== id);
            Swal.fire({
              icon: 'success',
              title: "Se ha elimiado el código correctamente!",
              showConfirmButton: false,
              timer: 1500
            });
          }
        });
      }
    });
  }

  async buildLineCode(code_type_id: any): Promise<void> {
    try {
      this.loadingFull = true;
      this.requestCodeForm.get('codeLineSelected')?.disable();
      this.requestCodeForm.get('codeFamilySelected')?.reset();
      this.requestCodeForm.get('codeFamilySelected')?.disable();
      this.requestCodeForm.get('codeSubFamilySelected')?.reset();
      this.requestCodeForm.get('codeSubFamilySelected')?.disable();
      if(await this.getCodeLines(code_type_id)) {
        this.requestCodeForm.get('codeLineSelected')?.enable();
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      this.loadingFull = false;
    }
  }

  async buildFamilyCode(code_line_id: any): Promise<void> {
    try {
      this.loadingFull = true;
      this.requestCodeForm.get('codeFamilySelected')?.disable();
      this.requestCodeForm.get('codeSubFamilySelected')?.reset();
      this.requestCodeForm.get('codeSubFamilySelected')?.disable();
      if(await this.getCodeFamilies(code_line_id)) {
        this.requestCodeForm.get('codeFamilySelected')?.enable();
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      this.loadingFull = false;
    }
  }

  async buildSubFamilyCode(code_family_id: any): Promise<void> {
    try {
      this.loadingFull = true;
      this.requestCodeForm.get('codeSubFamilySelected')?.disable();
      if(await this.getCodeSubFamilies(code_family_id)) {
        this.requestCodeForm.get('codeSubFamilySelected')?.enable();
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      this.loadingFull = false;
    }
  }

  CleanAndResetForm() {
    this.requestCodeForm.reset();
    this.codes = [];
  }

  validateCodesArray(control: FormControl) {
    const codesArray = control.value;
    return codesArray && codesArray.length > 0 ? null : { required: true };
  }

  validateSelectedOptions(control: FormControl) {
    const selectedOptions = control.value;
    return selectedOptions && selectedOptions.length > 0 ? null : { required: true };
  }

  async createRequestCode(): Promise<void> {
    try {
      if(this.requestCodeForm.invalid) {
        this.requestCodeForm.markAllAsTouched();
      }

      if(!this.validateChecks()) {
        Swal.fire({
          position: 'top-end',
          icon: 'error',
          title: 'Debe marcar al menos un artículo en su solicitud',
          showConfirmButton: false,
          timer: 5000,
          toast: true
        });
        return;
      }
      
      if(this.codes.length === 0) {
        Swal.fire({
          position: 'top-end',
          icon: 'error',
          title: 'Debe ingresar códigos en su solicitud',
          showConfirmButton: false,
          timer: 5000,
          toast: true
        });
        return;
      }
      Swal.fire({
        title: "Está a punto de enviar una nueva solicitud, ¿Está seguro de realizar esta acción?",
        icon: 'warning',
        width: 800,
        customClass: {
          popup: 'custom-swal-popup'
        },
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí',
        cancelButtonText: 'Cancelar',
        allowOutsideClick: false
      }).then(async (result) => {
        if (result.isConfirmed) {
          this.loadingFull = true;
          const body = new FormData();
          body.append('action', "INSERT");
          body.append('table_name', "CODE_REQUEST");
          body.append('request_user_id', localStorage.getItem('IdUser') ?? '');
          body.append('company_id', localStorage.getItem('IdCompany') ?? '');
          body.append('inventory_item', this.requestCodeForm.value.inventoryItemCheck == true ? '1' : '0');
          body.append('inventory_sales', this.requestCodeForm.value.inventorySalesCheck == true ? '1' : '0');
          body.append('inventory_shopping', this.requestCodeForm.value.inventoryShoppingCheck == true ? '1' : '0');
          body.append('accounting_group_id', this.requestCodeForm.value.accountingGroupSelected);
          body.append('commercial_group_id', this.requestCodeForm.value.comercialGroupSelected);
          body.append('financial_group_id', this.requestCodeForm.value.financialGroupSelected);
          body.append('executive_group_id', this.requestCodeForm.value.executiveGroupSelected);
          body.append('management_method_id', this.requestCodeForm.value.managementMethodSelected);
          body.append('origin_id', this.requestCodeForm.value.originSelected);
          body.append('code_type_id', this.requestCodeForm.value.codeTypeSelected);
          body.append('code_line_id', this.requestCodeForm.value.codeLineSelected);
          body.append('code_family_id', this.requestCodeForm.value.codeFamilySelected);
          body.append('code_subfamily_id', this.requestCodeForm.value.codeSubFamilySelected);
          body.append('warehouses', this.requestCodeForm.value.warehouseSelected);
          body.append('codes', JSON.stringify(this.codes));
      
          const response: any = await firstValueFrom(
            this.crudService.crud(body, environment.CreateRequestCode)
          );
          this.loadingFull = false;
          if(response.status === 1) {
            this.CleanAndResetForm();
            Swal.fire({
              icon: 'success',
              title: 'Registro de solicitud creada exitosamente',
              showConfirmButton: false,
              timer: 2000
            });
          }
        }
      });
    } catch (error) {
      this.loadingFull = false;
      console.log('[createRequestCode] Error: ', error);
    }
  }

  async getRequestCodeBySerialCode(serial_code: any): Promise<any> {
    const body = new FormData();
    body.append('serial_code', serial_code);
    return await firstValueFrom(
      this.crudService.crud(body, environment.GetRequestCodeBySerialCode)
    );
  }

  async searchRegisterBySerialCode(): Promise<void> {
    this.loadingFull = true;
    const serial_code = this.requestCodeForm.value.searchCode;
    const response = await this.getRequestCodeBySerialCode(serial_code);
    if(response && response.data && response.data.length === 0) {
      this.loadingFull = false;
      Swal.fire({
        icon: 'error',
        title: 'No se ha encontrado el registro con el código serial ingresado',
        showConfirmButton: false,
        timer: 3000
      });
    } else {
      Swal.fire({
        title: "Toda la información rellenada con anterioridad será reemplazada por el código a buscar, ¿Está seguro de realizar esta acción?",
        icon: 'warning',
        width: 800,
        customClass: {
          popup: 'custom-swal-popup'
        },
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí',
        cancelButtonText: 'Cancelar',
        allowOutsideClick: false
      }).then((result) => {
        if (result.isConfirmed) {
          const register = response.data[0];
          this.requestCodeForm.get('codeLineSelected')?.enable();
          this.buildLineCode(register.code_type_id);
          this.requestCodeForm.get('codeFamilySelected')?.enable();
          this.buildFamilyCode(register.code_line_id);
          this.requestCodeForm.get('codeSubFamilySelected')?.enable();
          this.buildSubFamilyCode(register.code_family_id);
    
          this.requestCodeForm.patchValue({
            accountingGroupSelected: register.accounting_group_id,
            comercialGroupSelected: register.commercial_group_id,
            financialGroupSelected: register.financial_group_id,
            executiveGroupSelected: register.executive_group_id,
            codeTypeSelected: register.code_type_id,
            codeLineSelected: register.code_line_id,
            codeFamilySelected: register.code_family_id,
            codeSubFamilySelected: register.code_subfamily_id,
            inventoryItemCheck: register.inventory_item == "1" ? true : false,
            inventorySalesCheck: register.inventory_sales == "1" ? true : false,
            inventoryShoppingCheck: register.inventory_shopping == "1" ? true : false,
            managementMethodSelected: register.management_method_id,
            originSelected: register.origin_id,
            warehouseSelected: register.warehouses.map((id: number) => id.toString()),
          });
          const id_company = localStorage.getItem('IdCompany');
          this.codes = Object.values(register.codes).map((code: any) => {
            return {
              ...code,
              id: this.actualId++,
              id_company,
            };
          });
    
          Swal.fire({
            position: 'top-end',
            icon: 'success',
            title: '¡Se ha encontrado el registro!',
            showConfirmButton: false,
            timer: 5000,
            toast: true
          });
        }
        this.loadingFull = false;
      });
    }
  }
}
